import { Badge } from "@/components/ui/Badge";
import { formatCount, percent } from "../stages";
import type { SearchStageData } from "../types";

const ScoreBar = ({ label, value }: { label: string; value: number }) => (
  <span className="flex items-center gap-2">
    <span className="h-1.5 w-16 overflow-hidden rounded-full bg-zinc-100" aria-hidden>
      <span className="block h-full bg-zinc-700" style={{ width: percent(value, 1) }} />
    </span>
    <span>
      <span className="sr-only">{label} </span>
      {percent(value, 1)}
    </span>
  </span>
);

/** Config optimization: the labelled sample, and the score of each revision. */
export const SearchStage = ({ data }: { data: SearchStageData }) => {
  const kept = data.revisions.filter((revision) => revision.kept);
  const best = kept[kept.length - 1];
  return (
    <div className="space-y-4">
      {data.labels && (
        <p className="text-sm text-zinc-700">
          {formatCount(data.labels.accept + data.labels.reject + data.labels.unresolved)} papers
          labelled: {formatCount(data.labels.accept)} accept, {formatCount(data.labels.reject)}{" "}
          reject, {formatCount(data.labels.unresolved)} unresolved
        </p>
      )}
      {!data.labels && data.revisions.length === 0 && (
        <p className="text-sm text-zinc-600">The agent is labelling a sample of papers.</p>
      )}
      {best && (
        <div className="space-y-1">
          <p className="text-sm text-zinc-700">
            Best kept revision {best.number}: precision{" "}
            <span className="font-semibold text-zinc-900">{percent(best.precision, 1)}</span>,
            recall <span className="font-semibold text-zinc-900">{percent(best.recall, 1)}</span>
          </p>
          {best.summary && <p className="text-xs text-zinc-600">{best.summary}</p>}
        </div>
      )}
      {data.revisions.length > 0 && (
        <div
          role="region"
          aria-label="Revisions of the search config"
          tabIndex={0}
          className="overflow-x-auto rounded-[3px] border border-zinc-200"
        >
          <table className="w-full text-left text-xs">
            <caption className="sr-only">The precision and recall of each revision</caption>
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">
                  Revision
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Change
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Precision
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Recall
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Result
                </th>
              </tr>
            </thead>
            <tbody>
              {data.revisions.map((revision) => (
                <tr key={revision.number} className="border-t border-zinc-100 text-zinc-700">
                  <th scope="row" className="px-3 py-2 font-medium text-zinc-900">
                    {revision.number}
                  </th>
                  <td className="px-3 py-2">
                    {revision.change || "—"}
                    {revision.summary && revision !== best && (
                      <span className="block text-zinc-500">{revision.summary}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <ScoreBar label="Precision" value={revision.precision} />
                  </td>
                  <td className="px-3 py-2">
                    <ScoreBar label="Recall" value={revision.recall} />
                  </td>
                  <td className="px-3 py-2">
                    {revision.kept ? <Badge variant="green">Kept</Badge> : <Badge>Not kept</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-zinc-500">
        Recall is measured only on the papers that the first step of the search finds. It
        does not count papers that the search misses.
      </p>
    </div>
  );
};
